/**
 * Government Solicitations — Dual-Pipeline Kanban Board
 *
 * Two pipelines:
 *   1. Sources Sought / RFI (pre-solicitation positioning)
 *   2. Active Solicitations (RFQ / RFP / IFB — live bids)
 *
 * Each card links to /gov/solicitations/:id
 * "+ New Solicitation" fetches metadata from SAM.gov by Notice ID,
 * shows an editable prefilled form, then POSTs to create.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, differenceInCalendarDays, parseISO } from 'date-fns';
import api from '../../services/api';

// ---------- Types ----------

type PipelineKey = 'sources_sought' | 'active';

interface Solicitation {
  id: string;
  title?: string | null;
  solicitation_number?: string | null;
  notice_id?: string | null;
  agency_name?: string | null;
  naics_code?: string | null;
  set_aside_code?: string | null;
  response_deadline?: string | null;
  pipeline?: PipelineKey | string;
  stage?: string | null;
  assigned_to?: string | null;
  assigned_user?: { id?: string; name?: string; email?: string } | null;
}

interface UserOption {
  id: string;
  name: string;
  email?: string;
}

interface StageDef {
  key: string;
  label: string;
  color: string;
  deadEnd?: boolean;
}

// ---------- Constants ----------

const OUR_NAICS = ['238160', '238110', '237310', '236220', '238990'];

const SET_ASIDE_OPTIONS = [
  { code: '', label: 'All Set-Asides' },
  { code: 'SBA', label: 'Small Business (SBA)' },
  { code: 'SB', label: 'Small Business (SB)' },
  { code: '8A', label: '8(a)' },
  { code: 'WOSB', label: 'WOSB' },
  { code: 'EDWOSB', label: 'EDWOSB' },
  { code: 'HZC', label: 'HUBZone' },
  { code: 'SDVOSBC', label: 'SDVOSB' },
  { code: 'VSA', label: 'VSA' },
  { code: 'NONE', label: 'None / Full & Open' },
];

const SOURCES_SOUGHT_STAGES: StageDef[] = [
  { key: 'identified', label: '🔍 Identified', color: '#64748B' },
  { key: 'go_no_go', label: '🎯 Go / No-Go', color: '#4A7EB5' },
  { key: 'engage_poc', label: '📞 Engage POC', color: '#3B82F6' },
  { key: 'teaming_assessment', label: '🤝 Teaming Assessment', color: '#6366F1' },
  { key: 'write_response', label: '✍️ Write Response', color: '#8B5CF6' },
  { key: 'submit_response', label: '📤 Submit Response', color: '#A855F7' },
  { key: 'confirm_receipt', label: '✅ Confirm Receipt', color: '#EC4899' },
  { key: 'request_follow_up', label: '🔁 Request Follow-Up', color: '#F59E0B' },
  { key: 'demo_capability_brief', label: '🎤 Demo / Capability Brief', color: '#F97316' },
  { key: 'confirm_solicitation_date', label: '📅 Confirm Sol. Date', color: '#0EA5E9' },
  { key: 'solicitation_released', label: '🚀 Solicitation Released', color: '#10B981' },
  { key: 'no_response_needed', label: '⏭️ No Response Needed', color: '#4A6580', deadEnd: true },
  { key: 'not_converted', label: '🚫 Not Converted', color: '#6B7280', deadEnd: true },
  { key: 'converted', label: '✨ Converted', color: '#10B981', deadEnd: true },
];

const ACTIVE_STAGES: StageDef[] = [
  { key: 'identified', label: '🔍 Identified', color: '#64748B' },
  { key: 'analyzing', label: '🔬 Analyzing', color: '#4A7EB5' },
  { key: 'go_no_go', label: '🎯 Go / No-Go', color: '#3B82F6' },
  { key: 'bid_prep', label: '📋 Bid Prep', color: '#6366F1' },
  { key: 'proposal_writing', label: '✍️ Proposal Writing', color: '#8B5CF6' },
  { key: 'bonding', label: '🛡️ Bonding', color: '#A855F7' },
  { key: 'submitted', label: '📤 Submitted', color: '#EC4899' },
  { key: 'proposal_verification', label: '🔎 Proposal Verification', color: '#F59E0B' },
  { key: 'monitoring_for_award', label: '👀 Monitoring for Award', color: '#0EA5E9' },
  { key: 'won', label: '🏆 Won', color: '#10B981' },
  { key: 'lost', label: '❌ Lost', color: '#EF4444', deadEnd: true },
  { key: 'no_bid', label: '🚫 No-Bid', color: '#6B7280', deadEnd: true },
  { key: 'cancelled', label: '⛔ Cancelled', color: '#4A6580', deadEnd: true },
];

const PIPELINE_TABS: { key: PipelineKey; label: string; icon: string }[] = [
  { key: 'active', label: 'Active Solicitations', icon: '🎯' },
  { key: 'sources_sought', label: 'Sources Sought / RFI', icon: '📡' },
];

// ---------- Helpers ----------

function getDeadlineMeta(deadline?: string | null): {
  label: string;
  color: string;
  bg: string;
  border: string;
} {
  if (!deadline) {
    return { label: 'No deadline', color: '#8FA8C0', bg: '#243447', border: '#2D4259' };
  }
  let d: Date;
  try {
    d = typeof deadline === 'string' ? parseISO(deadline) : new Date(deadline);
    if (isNaN(d.getTime())) throw new Error('bad');
  } catch {
    return { label: 'No deadline', color: '#8FA8C0', bg: '#243447', border: '#2D4259' };
  }
  const days = differenceInCalendarDays(d, new Date());
  const dateStr = format(d, 'MMM d');
  if (days < 0) return { label: `⚠️ Overdue · ${dateStr}`, color: '#fff', bg: '#7F1D1D', border: '#EF4444' };
  if (days < 7) return { label: `🔥 ${days}d · ${dateStr}`, color: '#fff', bg: '#B91C1C', border: '#EF4444' };
  if (days < 14) return { label: `⏰ ${days}d · ${dateStr}`, color: '#fff', bg: '#C2410C', border: '#F97316' };
  if (days < 30) return { label: `⏳ ${days}d · ${dateStr}`, color: '#111827', bg: '#FCD34D', border: '#F59E0B' };
  return { label: `${days}d · ${dateStr}`, color: '#8FA8C0', bg: '#243447', border: '#2D4259' };
}

function initials(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '—';
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// ---------- Component ----------

const GovSolicitationsPage: React.FC = () => {
  const navigate = useNavigate();

  const [pipeline, setPipeline] = useState<PipelineKey>('active');
  const [items, setItems] = useState<Solicitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [naicsFilter, setNaicsFilter] = useState('');
  const [setAsideFilter, setSetAsideFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [users, setUsers] = useState<UserOption[]>([]);

  // New solicitation modal
  const [showNew, setShowNew] = useState(false);
  const [fetchNoticeId, setFetchNoticeId] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [newForm, setNewForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const stages = pipeline === 'sources_sought' ? SOURCES_SOUGHT_STAGES : ACTIVE_STAGES;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { pipeline };
      if (naicsFilter) params.naics = naicsFilter;
      if (setAsideFilter) params.set_aside = setAsideFilter;
      if (assigneeFilter) params.assigned_to = assigneeFilter;
      if (search.trim()) params.search = search.trim();

      const { data } = await api.get('/gov/solicitations', { params });
      // Accept either { solicitations: [...] } or [...]
      const list: Solicitation[] = Array.isArray(data)
        ? data
        : data.solicitations || data.items || data.data || [];
      setItems(list);
    } catch (e: any) {
      console.error('Failed to load solicitations:', e);
      setError(e?.response?.data?.error || e?.message || 'Failed to load');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pipeline, naicsFilter, setAsideFilter, assigneeFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load users for assignee filter (best-effort)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/users');
        const list: any[] = Array.isArray(data) ? data : data.users || [];
        setUsers(
          list.map((u) => ({
            id: u.id,
            name: u.name || u.full_name || u.email || 'Unnamed',
            email: u.email,
          }))
        );
      } catch {
        // silent — filter just won't have options
      }
    })();
  }, []);

  // Read the stage field based on active pipeline (backend has ss_stage and active_stage columns)
  const stageOf = (item: any): string => {
    if (pipeline === 'sources_sought') return item.ss_stage || 'identified';
    if (pipeline === 'active') return item.active_stage || 'identified';
    return item.stage || 'identified';
  };

  // Group items by stage
  const byStage = useMemo(() => {
    const map: Record<string, Solicitation[]> = {};
    for (const s of stages) map[s.key] = [];
    for (const item of items) {
      const key = stageOf(item);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, stages, pipeline]);

  // ---------- Drag & Drop ----------

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDragging(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    setDragging(null);
    if (!id) return;

    const original = items;
    // Optimistic update — write to correct stage column for this pipeline
    const stageField = pipeline === 'sources_sought' ? 'ss_stage' : pipeline === 'active' ? 'active_stage' : 'stage';
    setItems((prev) => prev.map((it: any) => (it.id === id ? { ...it, [stageField]: stageKey } : it)));

    try {
      // Server enforces checklist. Returns 400 if incomplete.
      await api.post(`/gov/solicitations/${id}/advance`, { to_stage: stageKey });
    } catch (err: any) {
      // Roll back optimistic update
      setItems(original);
      const resp = err?.response?.data;
      if (resp?.checklist_incomplete) {
        const unmet: string[] = resp.unmet_items || [];
        alert(
          `Checklist incomplete for current stage. Missing items:\n\u2022 ${unmet.join('\n\u2022 ')}\n\nOpen the solicitation to complete the checklist before advancing.`
        );
      } else {
        alert(`Failed to move card: ${resp?.error || err?.message || 'unknown error'}`);
      }
    }
  };

  // ---------- New Solicitation ----------

  const openNew = () => {
    setShowNew(true);
    setFetchNoticeId('');
    setFetchError(null);
    setNewForm({});
  };

  const closeNew = () => {
    setShowNew(false);
    setFetchNoticeId('');
    setFetchError(null);
    setNewForm({});
    setFetching(false);
    setSaving(false);
  };

  const handleFetchSam = async () => {
    const nid = fetchNoticeId.trim();
    if (!nid) {
      setFetchError('Enter a SAM.gov Notice ID');
      return;
    }
    setFetching(true);
    setFetchError(null);
    try {
      const { data } = await api.post(`/gov/solicitations/fetch/${encodeURIComponent(nid)}`);
      // Backend returns { prefill: {...} } with SAM.gov metadata + resources[]
      const meta = data?.prefill || data?.solicitation || data?.data || data?.metadata || data || {};
      setNewForm({
        sam_notice_id: meta.sam_notice_id || meta.notice_id || nid,
        title: meta.title || '',
        solicitation_number: meta.solicitation_number || '',
        agency_name: meta.agency_name || meta.agency || '',
        sub_agency_name: meta.sub_agency_name || '',
        office_name: meta.office_name || '',
        naics_code: meta.naics_code || meta.naics || '',
        naics_description: meta.naics_description || '',
        classification_code: meta.classification_code || '',
        classification_description: meta.classification_description || '',
        set_aside_code: meta.set_aside_code || meta.set_aside || '',
        set_aside_description: meta.set_aside_description || '',
        response_deadline:
          (meta.response_deadline && String(meta.response_deadline).slice(0, 10)) || '',
        posted_date: meta.posted_date || '',
        description: meta.description || '',
        pipeline: meta.pipeline || pipeline,
        opportunity_type: meta.opportunity_type || '',
        place_of_performance: meta.place_of_performance || null,
        place_of_performance_display: meta.place_of_performance_display || '',
        // POC — these were being dropped, causing empty POC block on detail page
        point_of_contact: meta.point_of_contact || null,
        primary_poc_name: meta.primary_poc_name || '',
        primary_poc_email: meta.primary_poc_email || '',
        primary_poc_phone: meta.primary_poc_phone || '',
        sam_ui_link: meta.sam_ui_link || '',
        additional_info_link: meta.additional_info_link || '',
        resources: Array.isArray(meta.resources) ? meta.resources : [],
      });
    } catch (e: any) {
      console.error('SAM.gov fetch failed:', e);
      const err = e?.response?.data?.error || e?.message || 'Fetch failed';
      const hint = e?.response?.data?.hint;
      setFetchError(hint ? `${err}\n\n${hint}` : err);
    } finally {
      setFetching(false);
    }
  };

  const updateForm = (k: string, v: string) => setNewForm((p) => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!newForm.title && !newForm.sam_notice_id) {
      setFetchError('Title or Notice ID required');
      return;
    }
    setSaving(true);
    setFetchError(null);
    try {
      const payload: any = {
        ...newForm,
        pipeline: newForm.pipeline || pipeline,
      };
      // If SAM.gov prefill came with resources, ask backend to download them
      if (Array.isArray((newForm as any).resources) && (newForm as any).resources.length > 0) {
        payload.download_resources = true;
        payload.resources = (newForm as any).resources;
      }
      const { data } = await api.post('/gov/solicitations', payload);
      const created: Solicitation | undefined =
        data?.solicitation || data?.data || (data?.id ? data : undefined);
      closeNew();
      if (created?.id) {
        navigate(`/gov/solicitations/${created.id}`);
      } else {
        fetchData();
      }
    } catch (e: any) {
      console.error('Create failed:', e);
      setFetchError(e?.response?.data?.error || e?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------

  return (
    <>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>📑 Solicitations</h1>
            <p style={styles.subtitle}>
              {items.length} in {PIPELINE_TABS.find((p) => p.key === pipeline)?.label}
            </p>
          </div>
          <button onClick={openNew} style={styles.addBtn}>
            + New Solicitation
          </button>
        </div>

        {/* Pipeline Toggle */}
        <div style={styles.tabs}>
          {PIPELINE_TABS.map((tab) => {
            const active = pipeline === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setPipeline(tab.key)}
                style={{ ...styles.tab, ...(active ? styles.tabActive : {}) }}
              >
                {tab.icon} {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filter Bar */}
        <div style={styles.filterBar}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍  Search title, sol. #, agency..."
            style={{ ...styles.input, flex: 2, minWidth: 260 }}
          />
          <select
            value={naicsFilter}
            onChange={(e) => setNaicsFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All NAICS</option>
            {OUR_NAICS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <select
            value={setAsideFilter}
            onChange={(e) => setSetAsideFilter(e.target.value)}
            style={styles.select}
          >
            {SET_ASIDE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All Bidders</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        {/* Board */}
        {loading ? (
          <div style={styles.loading}>Loading solicitations…</div>
        ) : error ? (
          <div style={styles.error}>Error: {error}</div>
        ) : (
          <div style={styles.board}>
            {stages.map((stage) => {
              const list = byStage[stage.key] || [];
              return (
                <div
                  key={stage.key}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.key)}
                  style={{
                    ...styles.column,
                    borderTopColor: stage.color,
                    opacity: stage.deadEnd ? 0.85 : 1,
                  }}
                >
                  <div style={styles.columnHeader}>
                    <span style={styles.columnTitle}>{stage.label}</span>
                    <span style={{ ...styles.columnCount, backgroundColor: stage.color }}>
                      {list.length}
                    </span>
                  </div>
                  <div style={styles.columnBody}>
                    {list.length === 0 ? (
                      <div style={styles.emptyColumn}>Drop here</div>
                    ) : (
                      list.map((sol) => {
                        const meta = getDeadlineMeta(sol.response_deadline);
                        const assigneeName =
                          sol.assigned_user?.name ||
                          users.find((u) => u.id === sol.assigned_to)?.name ||
                          null;
                        return (
                          <div
                            key={sol.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, sol.id)}
                            onClick={() => navigate(`/gov/solicitations/${sol.id}`)}
                            style={{
                              ...styles.card,
                              borderLeftColor: stage.color,
                              opacity: dragging === sol.id ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.borderColor = stage.color)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.borderColor = '#2D4259')
                            }
                          >
                            <div style={styles.cardTitle}>
                              {truncate(sol.title || '(untitled)', 72)}
                            </div>
                            {sol.solicitation_number && (
                              <div style={styles.solNumber}>{sol.solicitation_number}</div>
                            )}
                            {sol.agency_name && (
                              <div style={styles.agency}>🏛️ {truncate(sol.agency_name, 40)}</div>
                            )}
                            <div style={styles.chipRow}>
                              {sol.naics_code && (
                                <span style={{ ...styles.chip, ...styles.chipNaics }}>
                                  {sol.naics_code}
                                </span>
                              )}
                              {sol.set_aside_code && (
                                <span style={{ ...styles.chip, ...styles.chipSetAside }}>
                                  {sol.set_aside_code}
                                </span>
                              )}
                            </div>
                            <div style={styles.cardFooter}>
                              <span
                                style={{
                                  ...styles.deadlinePill,
                                  color: meta.color,
                                  background: meta.bg,
                                  borderColor: meta.border,
                                }}
                              >
                                {meta.label}
                              </span>
                              <span
                                title={assigneeName || 'Unassigned'}
                                style={{
                                  ...styles.avatar,
                                  background: assigneeName ? '#4A7EB5' : '#2D4259',
                                }}
                              >
                                {initials(assigneeName)}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Solicitation Modal */}
      {showNew && (
        <div style={styles.modalOverlay} onClick={closeNew}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>+ New Solicitation</h2>
              <button onClick={closeNew} style={styles.modalClose}>
                ×
              </button>
            </div>

            {fetchError && <div style={{ ...styles.errorBanner, whiteSpace: 'pre-line' as const }}>{fetchError}</div>}

            {/* Fetch row */}
            <label style={styles.label}>SAM.gov Notice ID</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                value={fetchNoticeId}
                onChange={(e) => setFetchNoticeId(e.target.value)}
                placeholder="e.g. 123abc456def..."
                style={{ ...styles.input, flex: 1 }}
              />
              <button
                onClick={handleFetchSam}
                disabled={fetching || !fetchNoticeId.trim()}
                style={{
                  ...styles.addBtn,
                  opacity: fetching || !fetchNoticeId.trim() ? 0.6 : 1,
                  cursor: fetching || !fetchNoticeId.trim() ? 'default' : 'pointer',
                }}
              >
                {fetching ? 'Fetching…' : 'Fetch from SAM.gov'}
              </button>
            </div>

            {/* Prefilled / editable form (visible even without fetch — manual entry allowed) */}
            <div style={styles.formGrid}>
              <div style={styles.formCell}>
                <label style={styles.label}>Title *</label>
                <input
                  value={newForm.title || ''}
                  onChange={(e) => updateForm('title', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formCell}>
                <label style={styles.label}>Solicitation Number</label>
                <input
                  value={newForm.solicitation_number || ''}
                  onChange={(e) => updateForm('solicitation_number', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formCell}>
                <label style={styles.label}>Agency</label>
                <input
                  value={newForm.agency_name || ''}
                  onChange={(e) => updateForm('agency_name', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formCell}>
                <label style={styles.label}>NAICS Code</label>
                <input
                  value={newForm.naics_code || ''}
                  onChange={(e) => updateForm('naics_code', e.target.value)}
                  placeholder="e.g. 238160"
                  style={styles.input}
                />
              </div>
              <div style={styles.formCell}>
                <label style={styles.label}>Set-Aside</label>
                <input
                  value={newForm.set_aside_code || ''}
                  onChange={(e) => updateForm('set_aside_code', e.target.value)}
                  placeholder="e.g. 8(a), SB"
                  style={styles.input}
                />
              </div>
              <div style={styles.formCell}>
                <label style={styles.label}>Response Deadline</label>
                <input
                  type="date"
                  value={newForm.response_deadline || ''}
                  onChange={(e) => updateForm('response_deadline', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={{ ...styles.formCell, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Pipeline</label>
                <select
                  value={newForm.pipeline || pipeline}
                  onChange={(e) => updateForm('pipeline', e.target.value)}
                  style={styles.select}
                >
                  <option value="active">Active Solicitations</option>
                  <option value="sources_sought">Sources Sought / RFI</option>
                </select>
              </div>
              <div style={{ ...styles.formCell, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={newForm.description || ''}
                  onChange={(e) => updateForm('description', e.target.value)}
                  rows={4}
                  style={{ ...styles.input, resize: 'vertical' as const }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20, alignItems: 'stretch' }}>
              <button onClick={closeNew} style={{ ...styles.cancelBtn, flex: 1 }}>
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !newForm.title}
                style={{
                  ...styles.addBtn,
                  flex: 1,
                  justifyContent: 'center',
                  opacity: saving || !newForm.title ? 0.6 : 1,
                  cursor: saving || !newForm.title ? 'default' : 'pointer',
                }}
              >
                {saving ? 'Creating…' : 'Create Solicitation →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ---------- Styles ----------

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: 24,
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    flexShrink: 0,
  },
  title: { fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 },
  subtitle: { fontSize: 13, color: '#8FA8C0', marginTop: 4 },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    backgroundColor: '#4A7EB5',
    color: '#fff',
    border: '1px solid #4A7EB5',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  tab: {
    padding: '10px 20px',
    backgroundColor: '#1A2B3C',
    border: '1px solid #2D4259',
    borderRadius: 10,
    color: '#8FA8C0',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  tabActive: { backgroundColor: '#4A7EB5', borderColor: '#4A7EB5', color: '#fff' },
  filterBar: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  input: {
    padding: '10px 14px',
    borderRadius: 10,
    background: '#1A2B3C',
    border: '1px solid #2D4259',
    color: '#fff',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    width: '100%',
  },
  select: {
    padding: '10px 12px',
    borderRadius: 10,
    background: '#1A2B3C',
    border: '1px solid #2D4259',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    minWidth: 160,
  },
  loading: { textAlign: 'center', padding: 60, color: '#8FA8C0' },
  error: { textAlign: 'center', padding: 60, color: '#EF4444' },
  board: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    overflowY: 'visible',
    paddingBottom: 24,
    alignItems: 'flex-start',
    flex: 1,
    minHeight: 'calc(100vh - 280px)',
  },
  column: {
    width: 260,
    flexShrink: 0,
    backgroundColor: '#1A2B3C',
    borderRadius: 14,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #2D4259',
    borderTop: '3px solid',
    maxHeight: 'calc(100vh - 240px)',
  },
  columnHeader: {
    padding: '12px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #2D4259',
  },
  columnTitle: { color: '#fff', fontSize: 13, fontWeight: 700 },
  columnCount: {
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 800,
    color: '#fff',
  },
  columnBody: {
    padding: 10,
    overflowY: 'auto',
    flex: 1,
  },
  emptyColumn: {
    textAlign: 'center',
    padding: 20,
    color: '#4A6580',
    fontSize: 12,
    border: '1px dashed #2D4259',
    borderRadius: 8,
  },
  card: {
    display: 'block',
    backgroundColor: '#243447',
    borderRadius: 10,
    padding: 12,
    border: '1px solid #2D4259',
    borderLeft: '3px solid',
    cursor: 'grab',
    transition: 'border-color 0.15s ease, opacity 0.15s ease',
    marginBottom: 8,
    boxSizing: 'border-box',
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    lineHeight: 1.35,
    marginBottom: 6,
  },
  solNumber: {
    fontSize: 11,
    fontWeight: 700,
    color: '#4A7EB5',
    marginBottom: 4,
    letterSpacing: 0.3,
  },
  agency: { fontSize: 11, color: '#8FA8C0', marginBottom: 8 },
  chipRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
  chip: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
    letterSpacing: 0.3,
  },
  chipNaics: {
    background: '#1E3A5F',
    color: '#93C5FD',
    border: '1px solid #4A7EB555',
  },
  chipSetAside: {
    background: '#3B0764',
    color: '#DDD6FE',
    border: '1px solid #8B5CF655',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
    gap: 8,
  },
  deadlinePill: {
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 10,
    border: '1px solid',
    whiteSpace: 'nowrap',
  },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    flexShrink: 0,
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.72)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    background: '#0F1923',
    border: '1px solid #1E3A5F',
    borderRadius: 16,
    padding: 28,
    width: '100%',
    maxWidth: 640,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: { color: '#fff', margin: 0, fontSize: 18, fontWeight: 800 },
  modalClose: {
    background: 'none',
    border: 'none',
    color: '#8FA8C0',
    fontSize: 26,
    cursor: 'pointer',
    lineHeight: 1,
  },
  errorBanner: {
    background: '#EF444422',
    border: '1px solid #EF4444',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#EF4444',
    fontSize: 13,
    marginBottom: 16,
  },
  label: {
    display: 'block',
    color: '#8FA8C0',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  formCell: { display: 'flex', flexDirection: 'column' },
  cancelBtn: {
    flex: 1,
    background: '#1A2B3C',
    color: '#8FA8C0',
    border: '1px solid #2D4259',
    borderRadius: 10,
    padding: '12px',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default GovSolicitationsPage;
